/** Slug e número extraídos do resourceId de um Pull Request. */
export interface PullResourceRef {
  slug: string;
  number: number;
}

/**
 * Lê o resourceId de um Pull Request, no formato `pr:<slug>:<número>`.
 *
 * O identificador é composto porque a API endereça o recurso por slug e número —
 * `/pulls/<slug>/<número>` —, enquanto os demais tipos usam um segmento só. Sem
 * decompor, a tela montaria `/pulls/pr:protheus:1/...`, que não existe.
 *
 * O slug aceita ponto e sublinhado além de hífen, porque nome de repositório os tem —
 * `api_vendas`, `totvs.protheus`. Com o charset só de hífen, um repositório assim não
 * casava, o chamador caía no caminho genérico e TODA chamada do review — arquivos, diff,
 * respostas, fonte, assistente, exclusão — dava 404 sem dizer por quê. Maiúscula segue
 * recusada: o cadastro gera o slug em minúsculas.
 *
 * Formato inesperado devolve null, e o chamador cai no caminho genérico: um 404 do
 * backend é diagnosticável; uma URL meio montada não.
 */
const PULL_RESOURCE_ID = /^pr:([a-z0-9][a-z0-9._-]*):(\d+)$/;

export function parsePullResourceId(resourceId: string): PullResourceRef | null {
  const match = PULL_RESOURCE_ID.exec(decodeSegment(resourceId));
  if (!match) { return null; }

  const number = Number(match[2]);
  if (!Number.isSafeInteger(number) || number <= 0) { return null; }

  return { slug: match[1], number };
}

/**
 * Lê o identificador aceitando a forma percent-encoded.
 *
 * O Angular entrega o parâmetro de rota já decodificado, mas nem todo caminho até aqui
 * passa por ele — um proxy pode repassar o segmento ainda codificado. Decodificar aqui
 * faz a leitura não depender de quem decodificou primeiro; um valor que não é
 * percent-encoding volta intacto.
 */
function decodeSegment(value: string): string {
  const raw = (value || '').trim();
  if (!raw.includes('%')) { return raw; }
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Rótulo curto do Pull Request para listagem, montado a partir do resourceId. */
export function pullResourceLabel(resourceId: string): string {
  const ref = parsePullResourceId(resourceId);
  return ref ? `${ref.slug} #${ref.number}` : resourceId;
}

const COMMIT_RESOURCE_ID = /^commit:([a-z0-9][a-z0-9._-]*):([0-9a-fA-F]{7,64})$/;

/** Referência a um commit dentro do resourceId `commit:<slug>:<revisão>`. */
export interface CommitResourceRef {
  slug: string;
  revision: string;
}

/**
 * Desmonta o resourceId de um review de commit.
 *
 * Mesma tolerância do Pull Request quanto a percent-encoding: nem todo caminho até aqui
 * passa pelo router, e a leitura não deve depender de quem decodificou primeiro.
 */
export function parseCommitResourceId(resourceId: string): CommitResourceRef | null {
  const match = COMMIT_RESOURCE_ID.exec(decodeSegment(resourceId));
  if (!match) { return null; }
  return { slug: match[1], revision: match[2] };
}

/** Rótulo curto de um review de commit: `protheus 65b0c5a`. */
export function commitResourceLabel(resourceId: string): string {
  const ref = parseCommitResourceId(resourceId);
  return ref ? `${ref.slug} ${ref.revision.slice(0, 7)}` : resourceId;
}
